import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpCircle,
  Loader2,
  AlertCircle,
  User,
  CreditCard,
  Receipt,
  FileText,
  ShieldCheck,
  CheckCircle,
  AlertTriangle,
  BookOpen,
  GraduationCap,
} from "lucide-react";
import { describeApiError } from "../../../lib/apiError";
import {
  useAdminGradeUpgradeDetailsQuery,
  useUpdateGradeUpgradeStatusMutation,
} from "../api/upgradeApi";
// The induction bundle is an ordinary payment once raised, so it reverifies
// through the same admin endpoint as every other payment on /finance/analysis.
import { useReverifyPaymentMutation } from "../../billing/api/billingApi";
import {
  useCommitteeMembersPoolQuery,
  useUpgradeCommitteeReviewsQuery,
  useAssignUpgradeCommitteeMutation,
  useUnassignUpgradeCommitteeMutation,
} from "../../committee-reviews/api/committeeReviewApi";

const naira = (n?: number | null) =>
  n == null ? "—" : `₦${Number(n).toLocaleString("en-NG")}`;

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

const STATUS_CLS: Record<string, string> = {
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  returned: "bg-amber-100 text-amber-700",
  under_review: "bg-blue-100 text-blue-700",
  awaiting_endorsements: "bg-violet-100 text-violet-700",
  submitted: "bg-indigo-100 text-indigo-700",
  pending_payment: "bg-amber-100 text-amber-700",
};

function StatusBadge({ status }: { status?: string }) {
  const s = (status ?? "submitted").toLowerCase();
  return (
    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold uppercase ${STATUS_CLS[s] ?? STATUS_CLS.submitted}`}>
      {(status ?? "submitted").replace("_", " ")}
    </span>
  );
}

function Section({ icon, title, children, action }: { icon: React.ReactNode; title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
      <div className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2.5">
        {icon}
        <span className="text-sm font-semibold">{title}</span>
        {action && <span className="ml-auto">{action}</span>}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

// Human labels + dot colours for each recorded review action.
const REVIEW_ACTION: Record<string, { label: string; dot: string }> = {
  submitted:             { label: "Submitted for review", dot: "bg-sky-500" },
  awaiting_endorsements: { label: "Reviewed — awaiting chapter & referee acceptance", dot: "bg-violet-500" },
  under_review:          { label: "Sent to committee review", dot: "bg-blue-500" },
  recommended:                 { label: "Recommended by the committee", dot: "bg-emerald-500" },
  not_recommended:             { label: "Not recommended by the committee", dot: "bg-orange-500" },
  committee_member_approved:   { label: "Committee member recommended approval", dot: "bg-emerald-500" },
  committee_member_rejected:   { label: "Committee member recommended rejection", dot: "bg-red-500" },
  returned:              { label: "Returned for editing", dot: "bg-orange-500" },
  reopened:         { label: "Reopened by applicant", dot: "bg-gray-400" },
  resubmitted:      { label: "Resubmitted for review", dot: "bg-sky-500" },
  approved:         { label: "Approved", dot: "bg-green-600" },
  rejected:         { label: "Rejected", dot: "bg-red-600" },
  chapter_endorsed: { label: "Endorsed by chapter", dot: "bg-green-500" },
  chapter_rejected: { label: "Rejected by chapter", dot: "bg-red-500" },
};

function ReviewTimeline({ reviews }: { reviews?: any[] }) {
  const rows = Array.isArray(reviews) ? reviews : [];
  return (
    <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2.5">
        <FileText className="w-4 h-4" />
        <span className="text-sm font-semibold">Review history</span>
      </div>
      <div className="p-4">
        {rows.length === 0 ? (
          <p className="text-xs text-gray-400">No review actions recorded yet.</p>
        ) : (
          <ol className="space-y-3">
            {rows.map((r: any) => {
              const meta = REVIEW_ACTION[r.action] ?? { label: r.action, dot: "bg-gray-400" };
              const who = r.actor?.name ?? (r.actor_role === "system" ? "System" : r.actor_role ?? "—");
              return (
                <li key={r.id} className="flex gap-3">
                  <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${meta.dot}`} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800">{meta.label}</p>
                    <p className="text-[11px] text-gray-400">
                      {who} · {fmtDate(r.created_at)}
                      {r.from_status && r.to_status && r.from_status !== r.to_status
                        ? ` · ${String(r.from_status).replace("_", " ")} → ${String(r.to_status).replace("_", " ")}`
                        : ""}
                    </p>
                    {r.remark && <p className="text-xs text-gray-600 mt-0.5 whitespace-pre-wrap">"{r.remark}"</p>}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}

/**
 * The post-recommendation bundle (upgrade charge + induction fee + event fees)
 * and every attempt at paying it, with a Reverify button on each unsettled one.
 *
 * It exists because OnePay settles late: it can decline a payment at the
 * counter — stamping our row 'failed' — and report the very same transaction as
 * successful minutes later. Starting a fresh attempt also cancels the previous
 * one, so the attempt the money actually went through on is routinely NOT the
 * current one. Reverifying re-asks OnePay about that specific attempt and, when
 * the gateway confirms it, settles the invoice, books the induction events and
 * approves the application — the same pipeline the callback would have run.
 *
 * Same endpoint the secretariat's Reverify button on /finance/analysis uses.
 */
function InductionPayments({ app, onSettled }: { app: any; onSettled: () => void }) {
  const attempts: any[] = Array.isArray(app?.induction_attempts) ? app.induction_attempts : [];
  const guests: any[] = Array.isArray(app?.induction_guests) ? app.induction_guests : [];
  const invoice = app?.induction_invoice;
  const [reverify] = useReverifyPaymentMutation();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  // Nothing to show until the applicant has at least started a payment.
  if (attempts.length === 0 && !invoice) return null;

  const paid = !!app?.induction_paid_at;

  const doReverify = async (paymentId: string) => {
    setBusyId(paymentId);
    setNote(null);
    try {
      const res: any = await reverify(paymentId).unwrap();
      setNote({ ok: res?.status === "paid", text: res?.message ?? "Reverification ran." });
      // Verification is synchronous, so the application is already updated —
      // pull it again so the badge, invoice and attempt list all agree.
      onSettled();
    } catch (e: any) {
      setNote({ ok: false, text: describeApiError(e, "Could not reverify this payment.") });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Section
      icon={<CreditCard className="w-4 h-4" />}
      title="Induction & event fees"
      action={
        <span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full ${paid ? "bg-green-500/20 text-green-50" : "bg-amber-500/30 text-amber-50"}`}>
          {paid ? "Paid" : "Outstanding"}
        </span>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <Field label="Invoice number" value={invoice?.invoice_number} />
        <Field label="Invoice status" value={invoice?.status} />
        <Field label="Total" value={naira(invoice?.total_amount != null ? Number(invoice.total_amount) / 100 : null)} />
        <Field label="Paid at" value={app?.induction_paid_at ? fmtDate(app.induction_paid_at) : "—"} />
      </div>

      {note && (
        <div className={`mb-3 rounded-lg border px-3 py-2 text-xs ${note.ok ? "bg-green-50 border-green-200 text-green-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
          {note.text}
        </div>
      )}

      {attempts.length === 0 ? (
        <p className="text-sm text-gray-400 italic">No payment attempt recorded yet.</p>
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-400 uppercase border-b bg-gray-50">
                <th className="px-3 py-2 font-semibold">Reference</th>
                <th className="px-3 py-2 font-semibold">Amount</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold">Attempted</th>
                <th className="px-3 py-2 font-semibold text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {attempts.map((a) => (
                <tr key={a.id} className="align-middle">
                  <td className="px-3 py-2">
                    <span className="font-mono text-xs text-gray-700">{a.reference ?? "—"}</span>
                    {a.is_current && (
                      <span className="ml-2 text-[10px] font-bold uppercase text-indigo-600">current</span>
                    )}
                    {a.receipt_number && (
                      <span className="block text-[11px] text-gray-400">Receipt {a.receipt_number}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {naira(a.amount != null ? Number(a.amount) / 100 : null)}
                    {/* Guest cards ride on the SAME transaction, so an admin
                        claiming a late-settling payment has to know the cards
                        were issued too — a guest whose card never issued cannot
                        be checked in at the gate. */}
                    {a.guests_charged > 0 && (
                      <span
                        className={`block text-[11px] ${
                          a.status === "paid" && a.guests_issued < a.guests_charged
                            ? "text-red-600 font-semibold"
                            : "text-gray-400"
                        }`}
                      >
                        incl. {a.guests_charged} guest card{a.guests_charged > 1 ? "s" : ""}
                        {a.status === "paid" && ` · ${a.guests_issued} issued`}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold uppercase ${
                        a.status === "paid"
                          ? "bg-green-100 text-green-700"
                          : a.status === "failed"
                          ? "bg-red-100 text-red-700"
                          : a.status === "cancelled"
                          ? "bg-gray-100 text-gray-600"
                          : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {a.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">{fmtDate(a.created_at)}</td>
                  <td className="px-3 py-2 text-right">
                    {a.can_reverify ? (
                      <button
                        onClick={() => doReverify(String(a.id))}
                        disabled={busyId === String(a.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-bold hover:bg-amber-600 disabled:opacity-50"
                      >
                        {busyId === String(a.id) && <Loader2 className="w-3 h-3 animate-spin" />}
                        {busyId === String(a.id) ? "Checking…" : "Reverify"}
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">Settled</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Cards actually issued. A guest is not an event registration — they
          attend on the member's card — so they appear here rather than on the
          delegate list. */}
      {guests.length > 0 && (
        <div className="mt-4">
          <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-2">
            Guest access cards ({guests.length})
          </p>
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-400 uppercase border-b bg-gray-50">
                  <th className="px-3 py-2 font-semibold">Guest</th>
                  <th className="px-3 py-2 font-semibold">Contact</th>
                  <th className="px-3 py-2 font-semibold">Fee</th>
                  <th className="px-3 py-2 font-semibold">Issued</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {guests.map((g) => (
                  <tr key={g.id}>
                    <td className="px-3 py-2 text-gray-800">{g.name || "—"}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {g.email || "—"}
                      {g.phone && <span className="block">{g.phone}</span>}
                    </td>
                    <td className="px-3 py-2">{naira(g.amount != null ? Number(g.amount) : null)}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">{fmtDate(g.issued_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!paid && attempts.length > 0 && (
        <p className="text-[11px] text-gray-400 mt-2">
          Reverify re-asks OnePay about that attempt. If the gateway confirms it, the fees are
          settled, the induction events are booked, any guest cards paid for are issued, and the
          application is approved automatically.
        </p>
      )}
    </Section>
  );
}

function Field({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-gray-400 font-semibold uppercase tracking-wide">{label}</span>
      <span className="text-sm text-gray-800">{value ?? <span className="text-gray-400 italic">—</span>}</span>
    </div>
  );
}

function Table({ heads, rows }: { heads: string[]; rows: (React.ReactNode)[][] }) {
  if (!rows || rows.length === 0) return <p className="text-sm text-gray-400 italic">No entries recorded.</p>;
  return (
    <div className="overflow-x-auto border rounded-lg">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-400 uppercase border-b bg-gray-50">
            {heads.map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r, i) => (
            <tr key={i}>{r.map((c, j) => <td key={j} className="px-3 py-2 text-gray-700 align-top">{c ?? "—"}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function subjectsText(subjects: any): string {
  if (!subjects || typeof subjects !== "object") return "—";
  return Object.entries(subjects).map(([k, v]) => `${k}: ${v}`).join(", ");
}

const STORAGE_BASE = "https://api.niqsng.org/storage";
// Guarded storage URL — passes through absolute URLs and strips a leading slash
// so an already-full or root-relative path doesn't produce a broken link.
const storageUrl = (p?: string | null): string | null =>
  !p ? null : String(p).startsWith("http") ? String(p) : `${STORAGE_BASE}/${String(p).replace(/^\//, "")}`;
/** One lodged document, openable — or named as missing. */
function DocRow({ label, path }: { label: string; path?: string | null }) {
  const url = storageUrl(path);

  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-gray-100 bg-gray-50">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800"
        >
          <FileText className="w-3.5 h-3.5" />
          Open
        </a>
      ) : (
        <span className="text-xs font-semibold text-amber-600">Not uploaded</span>
      )}
    </div>
  );
}

// The uploaded certificate can arrive under a few different keys — read them all.
const certOf = (row: any): string | null =>
  row?.cert_file ?? row?.certificate_path ?? row?.cert_url ?? row?.file ?? null;


const num = (n: any) => Math.round(Number(n ?? 0) * 100) / 100;

/**
 * Warns when a member's counted CPD leans on pending self-declared claims that
 * can shrink (awarded points become the attached event's, often lower) or vanish
 * (rejected) on review — so an approval here could upgrade a member who does not
 * actually hold the required CPD. Fed by `cpd_review` on the admin show payload.
 */
function CpdReviewBanner({ review }: { review?: any }) {
  // No block if the backend has no CPD requirement for this route, or the field
  // is absent (older API build) — nothing to assert either way.
  if (!review || review.required == null) return null;

  const { required, awarded, effective, pending_points, pending_count, relies_on_pending, below_minimum, pending_claims } = review;

  if (!relies_on_pending && !below_minimum) {
    return (
      <div className="flex items-start gap-2 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
        <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          CPD confirmed — <b>{num(awarded)}</b> of {num(required)} required units are already approved in the ledger.
        </span>
      </div>
    );
  }

  const tone = below_minimum
    ? { border: "border-red-300", bg: "bg-red-50", text: "text-red-800", chip: "bg-red-100 text-red-700" }
    : { border: "border-amber-300", bg: "bg-amber-50", text: "text-amber-900", chip: "bg-amber-100 text-amber-800" };

  return (
    <div className={`rounded-xl border ${tone.border} ${tone.bg} px-4 py-3 ${tone.text}`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <div className="space-y-2 w-full">
          <p className="text-sm font-semibold">
            {below_minimum ? "CPD is below the required minimum" : "Eligibility rests on unconfirmed CPD claims"}
          </p>
          <p className="text-xs leading-relaxed">
            {below_minimum ? (
              <>Even counting pending claims this member has <b>{num(effective)}</b> of the <b>{num(required)}</b> units required — some claims were likely downgraded or rejected since they applied. They no longer meet the CPD requirement.</>
            ) : (
              <>Only <b>{num(awarded)}</b> of the <b>{num(required)}</b> required units are confirmed in the ledger. The rest rely on {pending_count} pending claim{pending_count === 1 ? "" : "s"} worth <b>{num(pending_points)}</b> self-declared point{num(pending_points) === 1 ? "" : "s"}. On review those become the attached event's value (often lower) or are rejected.</>
            )}
          </p>
          <div className="flex flex-wrap gap-2 text-[11px] font-semibold">
            <span className={`px-2 py-0.5 rounded-full ${tone.chip}`}>Confirmed {num(awarded)}</span>
            <span className={`px-2 py-0.5 rounded-full ${tone.chip}`}>Pending {num(pending_points)} ({pending_count})</span>
            <span className={`px-2 py-0.5 rounded-full ${tone.chip}`}>Effective {num(effective)}</span>
            <span className={`px-2 py-0.5 rounded-full ${tone.chip}`}>Required {num(required)}</span>
          </div>

          {Array.isArray(pending_claims) && pending_claims.length > 0 && (
            <div className="mt-1 rounded-lg border border-black/5 bg-white/70 divide-y divide-black/5">
              {pending_claims.map((c: any) => (
                <div key={c.id} className="flex items-center justify-between gap-3 px-3 py-1.5 text-xs">
                  <span className="truncate">
                    {c.title}
                    {c.is_manual && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-black/5 text-[10px] uppercase tracking-wide">manual</span>}
                  </span>
                  <span className="shrink-0 font-semibold">{num(c.cpd_points_claimed)} pts · {c.activity_date ?? "—"}</span>
                </div>
              ))}
            </div>
          )}
          <p className="text-[11px] opacity-80">
            Approving while confirmed CPD is short requires an explicit override in the decision panel.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function AdminUpgradeApplicationDetailPage() {
  const { id = "" } = useParams();
  const { data: app, isLoading, isError, refetch } =
    useAdminGradeUpgradeDetailsQuery(id, { skip: !id });
  const [update, { isLoading: saving }] = useUpdateGradeUpgradeStatusMutation();

  const [remark, setRemark] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Set when the backend blocks an approval because the CPD minimum is met only
  // by unconfirmed claims; unlocks the explicit "approve anyway" override.
  const [needsCpdOverride, setNeedsCpdOverride] = useState(false);

  // ── Membership Committee assignment ──────────────────────────────────
  // Both committee verdicts count: a member's decision moves the application to
  // recommended / not_recommended, and gating on 'under_review' alone made the
  // committee panel — and the Return button with it — disappear the moment the
  // first one landed.
  const isUnderReview = ["under_review", "recommended", "not_recommended"].includes(String(app?.status));
  const { data: committeeReviews = [], refetch: refetchReviews } =
    useUpgradeCommitteeReviewsQuery(id as string, { skip: !id || !isUnderReview });
  const [assignOpen, setAssignOpen] = useState(false);
  const { data: pool } = useCommitteeMembersPoolQuery(undefined, { skip: !assignOpen });
  const [pickedMembers, setPickedMembers] = useState<string[]>([]);
  const [assignCommittee, { isLoading: assigning }] = useAssignUpgradeCommitteeMutation();
  const [unassignCommittee] = useUnassignUpgradeCommitteeMutation();

  const doAssignCommittee = async () => {
    if (!pickedMembers.length) return;
    setErr(null);
    try {
      await assignCommittee({ id: id as string, member_ids: pickedMembers }).unwrap();
      setAssignOpen(false);
      setPickedMembers([]);
      refetchReviews();
    } catch (e: any) {
      setErr(describeApiError(e, "Could not assign committee members."));
    }
  };
  const doUnassignCommittee = async (reviewId: number) => {
    setErr(null);
    try {
      await unassignCommittee({ id: id as string, reviewId }).unwrap();
      refetchReviews();
    } catch (e: any) {
      setErr(describeApiError(e, "Could not remove committee member."));
    }
  };

  useEffect(() => {
    if (app?.remark) setRemark(app.remark);
  }, [app?.remark]);

  const act = async (target: string, cpdOverride = false) => {
    setMsg(null);
    setErr(null);
    if (target === "returned" && !remark.trim()) {
      setErr("Add a note telling the applicant what to correct before returning it.");
      return;
    }
    if (target === "rejected" && !window.confirm("Rejection is final and cannot be undone. Continue?")) {
      return;
    }
    try {
      await update({ id, status: target, remark: remark || undefined, cpd_override: cpdOverride || undefined }).unwrap();
      setMsg("Status updated.");
      setNeedsCpdOverride(false);
      refetch();
    } catch (e: any) {
      if (e?.data?.code === "CPD_NOT_CONFIRMED") {
        setNeedsCpdOverride(true);
        setErr(e.data.message || "This member's CPD is not fully confirmed.");
      } else {
        setNeedsCpdOverride(false);
        setErr(describeApiError(e, "Could not update status."));
      }
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6 py-24 flex flex-col items-center gap-3 text-gray-400">
        <Loader2 className="w-7 h-7 animate-spin" />
        <p className="text-sm">Loading application…</p>
      </div>
    );
  }

  if (isError || !app) {
    return (
      <div className="max-w-4xl mx-auto p-6 py-20 text-center space-y-3">
        <AlertCircle className="w-8 h-8 text-red-400 mx-auto" />
        <p className="text-sm text-red-500">Could not load this application.</p>
        <Link to="/admin/upgrade-applications" className="inline-block px-4 py-2 rounded-lg border text-sm hover:bg-gray-50">
          Back to list
        </Link>
      </div>
    );
  }

  const payment = app.payment;
  const invoice = app.invoice;

  // Prerequisites to send an application into committee review.
  const chapterEndorsed = !!app.chapter_accepted_at;
  const approvedReferees = (app.referees ?? []).filter(
    (r: any) => String(r?.decision).toLowerCase() === "approved",
  ).length;
  const reviewReady = chapterEndorsed && approvedReferees >= 2;

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-5">
      <Link
        to={app.to_category?.id ? `/admin/upgrade-applications/category/${app.to_category.id}` : "/admin/upgrade-applications"}
        state={app.to_category?.name ? { categoryName: app.to_category.name } : undefined}
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800"
      >
        <ArrowLeft className="w-4 h-4" />
        {app.to_category?.name ? `Back to ${app.to_category.name} upgrades` : "All upgrade applications"}
      </Link>

      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-indigo-600 to-blue-700 text-white p-6 shadow-lg">
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 text-indigo-100 text-xs font-semibold mb-1.5 bg-white/10 px-2.5 py-1 rounded-full">
              <ArrowUpCircle className="w-3.5 h-3.5" /> Grade Upgrade · #{String(app.id).padStart(6, "0")}
            </div>
            <h1 className="text-2xl font-bold">
              {app.from_category?.name ?? "—"} → {app.to_category?.name ?? "—"}
              {app.route && <span className="text-base font-medium capitalize"> · {app.route} route</span>}
            </h1>
            <p className="text-sm text-indigo-100 mt-1">
              {app.user?.name} · submitted {fmtDate(app.created_at)}
            </p>
          </div>
          <StatusBadge status={app.status} />
        </div>
      </div>

      <CpdReviewBanner review={app.cpd_review} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {/* Member */}
          <Section icon={<User className="w-4 h-4" />} title="Member">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Name" value={app.user?.name} />
              <Field label="Email" value={app.user?.email} />
              <Field label="Membership No." value={app.user?.member?.membership_no} />
            </div>
          </Section>

          {/* Payment */}
          <Section icon={<CreditCard className="w-4 h-4" />} title="Payment">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Fee" value={naira(app.amount != null ? Number(app.amount) / 100 : null)} />
              <Field label="Payment status" value={payment?.status} />
              <Field label="Receipt number" value={payment?.receipt_number} />
              <Field label="Paid at" value={payment?.paid_at ? fmtDate(payment.paid_at) : "—"} />
              <Field label="Reference" value={app.reference} />
              <Field label="Gateway" value={payment?.gateway} />
            </div>
          </Section>

          {/* Invoice */}
          {invoice && (
            <Section icon={<Receipt className="w-4 h-4" />} title="Invoice">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Invoice number" value={invoice.invoice_number} />
                <Field label="Status" value={invoice.status} />
                <Field label="Total" value={naira(invoice.total_amount != null ? Number(invoice.total_amount) / 100 : null)} />
                <Field label="Balance" value={naira(invoice.balance != null ? Number(invoice.balance) / 100 : null)} />
              </div>
            </Section>
          )}

          {/* Post-recommendation bundle + its payment attempts */}
          <InductionPayments app={app} onSettled={refetch} />

          {/* Applicant details (step 1) */}
          <Section icon={<FileText className="w-4 h-4" />} title="Applicant details">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full name" value={[app.title, app.surname, app.other_names].filter(Boolean).join(" ") || "—"} />
              <Field label="Gender" value={app.gender} />
              <Field label="Date of birth" value={app.date_of_birth ? fmtDate(app.date_of_birth) : "—"} />
              <Field label="Nationality" value={app.nationality} />
              <Field label="Phone" value={app.phone} />
              <Field label="Email" value={app.email} />
              <Field label="Residential address" value={app.residential_address} />
              <Field label="Postal address" value={app.postal_address} />
              <Field label="Chapter" value={app.chapter?.name ?? app.chapter_other} />
              <Field label="Form completion" value={`Step ${app.completion_step ?? 0} of 8`} />
              <Field label="Acknowledged" value={app.acknowledged_at ? fmtDate(app.acknowledged_at) : "Not yet"} />
            </div>
          </Section>

          {/* Qualifications (step 2) */}
          <Section icon={<FileText className="w-4 h-4" />} title="Academic qualifications">
            <Table
              heads={["Institution", "Qualification", "Year", "Certificate"]}
              rows={(app.qualifications ?? []).map((q: any) => [
                q.institution, q.qualification, q.year,
                storageUrl(certOf(q)) ? <a className="text-indigo-600 hover:underline" href={storageUrl(certOf(q))!} target="_blank" rel="noreferrer">View</a> : "—",
              ])}
            />
          </Section>

          {/* O'level (step 3) */}
          <Section icon={<FileText className="w-4 h-4" />} title="O'level results">
            <Table
              heads={["Exam", "Year", "Exam No.", "Subjects", "Result"]}
              rows={(app.olevel_results ?? []).map((r: any) => [
                String(r.exam_type ?? "").toUpperCase(), r.exam_year, r.exam_number, subjectsText(r.subjects),
                storageUrl(r.result_file) ? <a className="text-indigo-600 hover:underline" href={storageUrl(r.result_file)!} target="_blank" rel="noreferrer">View</a> : "—",
              ])}
            />
          </Section>

          {/* Prelim / IJMB results (optional, step 3) */}
          {(app.prelim_results ?? []).length > 0 && (
            <Section icon={<FileText className="w-4 h-4" />} title="Prelim / IJMB results">
              <Table
                heads={["Exam", "Year", "Institution", "Exam No.", "Subjects", "Result"]}
                rows={(app.prelim_results ?? []).map((p: any) => [
                  p.exam_type === "others" ? (p.other_exam_name || "Others") : String(p.exam_type ?? "").toUpperCase(),
                  p.exam_year, p.institution, p.exam_number, subjectsText(p.subjects),
                  storageUrl(p.result_file) ? <a className="text-indigo-600 hover:underline" href={storageUrl(p.result_file)!} target="_blank" rel="noreferrer">View</a> : "—",
                ])}
              />
            </Section>
          )}

          {/* Professional memberships (step 4) */}
          <Section icon={<FileText className="w-4 h-4" />} title="Professional memberships">
            <Table
              heads={["Body", "Stage passed", "Certificate"]}
              rows={(app.professional_memberships ?? []).map((m: any) => [
                m.body_name, m.stage_passed,
                storageUrl(m.certificate_path) ? <a className="text-indigo-600 hover:underline" href={storageUrl(m.certificate_path)!} target="_blank" rel="noreferrer">View</a> : "—",
              ])}
            />
          </Section>

          {/* Experience (step 5) */}
          <Section icon={<FileText className="w-4 h-4" />} title="Work experience">
            <Table
              heads={["Organization", "Position", "From", "To", "Responsibilities"]}
              rows={(app.experiences ?? []).map((x: any) => [
                x.organization, x.position, x.start_date ? fmtDate(x.start_date) : "—",
                x.end_date ? fmtDate(x.end_date) : "Present", x.responsibilities,
              ])}
            />
          </Section>

          {/* Seminars (step 6) */}
          <Section icon={<FileText className="w-4 h-4" />} title="Seminars & training">
            <Table
              heads={["Title", "Date", "Location"]}
              rows={(app.seminars ?? []).map((s: any) => [s.title, s.date ? fmtDate(s.date) : "—", s.location])}
            />
          </Section>

          {/* Referees (step 7) */}
          <Section icon={<User className="w-4 h-4" />} title="Referees">
            <Table
              heads={["Referee", "Membership No.", "Relationship", "Decision"]}
              rows={(app.referees ?? []).map((r: any) => [
                [r.member?.surname, r.member?.othernames].filter(Boolean).join(" ") || r.member_id,
                r.member?.membership_no, r.relationship,
                <StatusBadge status={r.decision} />,
              ])}
            />
          </Section>

          {/* Fellowship documents — shown only where they apply, and only
              once something has been lodged. An empty section on every
              probationer→member application would be noise; a MISSING one on
              a fellowship application is the point, so the rows say "Not
              uploaded" rather than the section disappearing. */}
          {(app.citation_file || app.qsrbn_licence_file) && (
            <Section icon={<FileText className="w-4 h-4" />} title="Fellowship documents">
              <div className="space-y-2">
                <DocRow label="Citation" path={app.citation_file} />
                <DocRow label="QSRBN practice licence" path={app.qsrbn_licence_file} />
              </div>
            </Section>
          )}

          {/* Applicant's logbooks */}
          <Section icon={<BookOpen className="w-4 h-4" />} title="Logbooks">
            <Table
              heads={["Diet", "Status", "Created"]}
              rows={(app.logbooks ?? []).map((l: any) => [
                l.diet?.title || "—",
                <StatusBadge status={l.status} />,
                l.created_at ? fmtDate(l.created_at) : "—",
              ])}
            />
          </Section>

          {/* Applicant's exams */}
          <Section icon={<GraduationCap className="w-4 h-4" />} title="Examinations">
            <Table
              heads={["Exam", "Diet", "Status", "Result"]}
              rows={(app.exam_applications ?? []).map((e: any) => [
                e.exam?.title || e.exam?.name || e.exam?.code || "—",
                e.diet?.name || e.diet?.title || "—",
                <StatusBadge status={e.status} />,
                e.result?.recommendation || e.result?.status || e.result?.grade || "—",
              ])}
            />
          </Section>
        </div>

        {/* Review action */}
        <div className="space-y-5">
          <div className="bg-white border rounded-xl shadow-sm overflow-hidden sticky top-4">
            <div className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2.5">
              <ShieldCheck className="w-4 h-4" />
              <span className="text-sm font-semibold">Review decision</span>
            </div>
            <div className="p-4 space-y-3">
              {/* Prerequisite checklist — must be met before committee review */}
              {["submitted", "awaiting_endorsements"].includes(app.status) && (
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 space-y-2">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Ready for committee review?</p>
                  <div className="flex items-center gap-2 text-xs">
                    {chapterEndorsed ? <CheckCircle className="w-4 h-4 text-green-600 shrink-0" /> : <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />}
                    <span className={chapterEndorsed ? "text-gray-700" : "text-amber-700"}>Chapter endorsement</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {approvedReferees >= 2 ? <CheckCircle className="w-4 h-4 text-green-600 shrink-0" /> : <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />}
                    <span className={approvedReferees >= 2 ? "text-gray-700" : "text-amber-700"}>Membership referees approved ({approvedReferees}/2)</span>
                  </div>
                </div>
              )}

              {/* Message / reason */}
              {["submitted", "awaiting_endorsements", "under_review", "recommended", "not_recommended"].includes(app.status) && (
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">
                    {["under_review", "recommended", "not_recommended"].includes(app.status) ? "Reason / remark" : "Message to applicant"}
                  </label>
                  <textarea
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                    rows={4}
                    placeholder="Required when returning for editing…"
                    className="mt-1 w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100 resize-none"
                  />
                </div>
              )}

              {msg && (
                <div className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                  <CheckCircle className="w-3.5 h-3.5" /> {msg}
                </div>
              )}
              {err && (
                <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>
              )}

              {/* ── Secretariat stage: submitted → vet it ── */}
              {app.status === "submitted" && (
                <div className="space-y-2">
                  <button
                    onClick={() => act("awaiting_endorsements")}
                    disabled={saving}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 transition-colors"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                    Mark reviewed — awaiting endorsements
                  </button>
                  <p className="text-[11px] text-gray-500 text-center">
                    Confirms you have vetted this application. It then waits for chapter endorsement and referee acceptance before committee review.
                  </p>
                  {reviewReady && (
                    <button
                      onClick={() => act("under_review")}
                      disabled={saving}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-indigo-300 text-indigo-700 text-sm font-semibold hover:bg-indigo-50 disabled:opacity-50 transition-colors"
                    >
                      <ShieldCheck className="w-4 h-4" /> Skip — send straight to committee review
                    </button>
                  )}
                  <button
                    onClick={() => act("returned")}
                    disabled={saving}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-amber-300 text-amber-700 text-sm font-semibold hover:bg-amber-50 disabled:opacity-50 transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" /> Return for editing
                  </button>
                </div>
              )}

              {/* ── Awaiting endorsements → send to committee once ready ── */}
              {app.status === "awaiting_endorsements" && (
                <div className="space-y-2">
                  <button
                    onClick={() => act("under_review")}
                    disabled={saving || !reviewReady}
                    title={reviewReady ? "" : "Chapter endorsement and 2 approved referees are required first"}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                    Send to committee review
                  </button>
                  {!reviewReady && (
                    <p className="text-[11px] text-amber-600 text-center">
                      Waiting on chapter endorsement and two approved referees.
                    </p>
                  )}
                  <button
                    onClick={() => act("returned")}
                    disabled={saving}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-amber-300 text-amber-700 text-sm font-semibold hover:bg-amber-50 disabled:opacity-50 transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" /> Return for editing
                  </button>
                </div>
              )}

              {/* ── Committee stage: under_review / recommended ── */}
              {isUnderReview && (
                <div className="space-y-3">
                  <button
                    onClick={() => setAssignOpen(true)}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors"
                  >
                    <ShieldCheck className="w-4 h-4" /> Assign to committee
                  </button>

                  {/* Committee recommendations — read-only (admin does not decide) */}
                  <div className="rounded-lg border border-gray-100 p-3">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                      Committee recommendations
                    </p>
                    {committeeReviews.length ? (
                      <div className="space-y-2">
                        {committeeReviews.map((r: any) => {
                          const cls =
                            r.decision === "approved"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : r.decision === "rejected"
                                ? "bg-red-50 text-red-700 border-red-200"
                                : "bg-amber-50 text-amber-700 border-amber-200";
                          return (
                            <div key={r.id} className="flex items-start justify-between gap-2 text-xs">
                              <div className="min-w-0">
                                <div className="font-semibold text-gray-800">{r.name}</div>
                                <span className={`inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full border mt-0.5 ${cls}`}>
                                  {r.decision}
                                </span>
                                {r.remark ? <div className="text-gray-500 mt-1">{r.remark}</div> : null}
                              </div>
                              {r.decision === "pending" && (
                                <button
                                  onClick={() => doUnassignCommittee(r.id)}
                                  className="text-red-500 hover:underline shrink-0"
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500">No committee members assigned yet.</p>
                    )}
                    <p className="text-[10px] text-gray-400 mt-2 leading-relaxed">
                      {app.status === "recommended"
                        ? "Recommended by the committee. The applicant now pays their upgrade, induction and event fees — settling that approves the application automatically."
                        : app.status === "not_recommended"
                          ? "The committee has recommended AGAINST this application. Nothing is payable; the chairman makes the final decision."
                          : "The committee chairman makes the final approve/reject decision after members review."}
                    </p>
                  </div>

                  <button
                    onClick={() => act("returned")}
                    disabled={saving}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-amber-300 text-amber-700 text-sm font-semibold hover:bg-amber-50 disabled:opacity-50 transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" /> Return for editing
                  </button>
                </div>
              )}

              {/* ── Terminal / waiting states ── */}
              {["approved", "rejected", "returned", "draft", "pending_payment"].includes(app.status) && (
                <div className="rounded-lg bg-gray-50 border border-gray-100 p-3 text-xs text-gray-600 leading-relaxed">
                  {app.status === "approved" && "This application has been approved."}
                  {app.status === "rejected" && "This application was rejected — the decision is final and cannot be reopened."}
                  {app.status === "returned" && "Returned to the applicant for editing — awaiting their correction and resubmission."}
                  {(app.status === "draft" || app.status === "pending_payment") && "The applicant has not yet completed and paid for this application."}
                </div>
              )}

              {app.reviewed_at && (
                <p className="text-[11px] text-gray-400 text-center">
                  Last reviewed {fmtDate(app.reviewed_at)}
                </p>
              )}
            </div>
          </div>

          <ReviewTimeline reviews={app.reviews} />
        </div>
      </div>

      {/* Assign committee members modal */}
      {assignOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white border shadow-xl p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Assign to committee</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {pool?.committee?.name
                    ? `Select ${pool.committee.name} members to review this application.`
                    : "Select committee members to review this application."}
                </p>
              </div>
              <button onClick={() => setAssignOpen(false)} className="px-2 py-1 rounded border hover:bg-gray-50">
                ✕
              </button>
            </div>

            <div className="mt-4 max-h-[360px] overflow-y-auto pr-1 space-y-2">
              {(pool?.members ?? []).length ? (
                (pool?.members ?? []).map((m: any) => {
                  const checked = pickedMembers.includes(m.member_id);
                  return (
                    <label
                      key={m.member_id}
                      className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer hover:bg-indigo-50/40 ${
                        checked ? "bg-indigo-50 border-indigo-200" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setPickedMembers((prev) =>
                            prev.includes(m.member_id)
                              ? prev.filter((x) => x !== m.member_id)
                              : [...prev, m.member_id],
                          )
                        }
                        className="h-4 w-4 accent-indigo-600"
                      />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-800 truncate">
                          {m.name}{" "}
                          <span className="text-[10px] text-gray-400 uppercase">({m.committee_role})</span>
                        </div>
                        {m.email ? <div className="text-xs text-gray-500 truncate">{m.email}</div> : null}
                      </div>
                    </label>
                  );
                })
              ) : (
                <div className="text-sm text-gray-500">No Membership Committee members found.</div>
              )}
            </div>

            <div className="mt-5 flex items-center justify-between gap-2">
              <div className="text-xs text-gray-600">
                Selected: <b>{pickedMembers.length}</b>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setAssignOpen(false)}
                  className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={doAssignCommittee}
                  disabled={assigning || !pickedMembers.length}
                  className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 text-sm disabled:opacity-50"
                >
                  {assigning ? "Assigning…" : "Assign"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
